import { Box, HStack, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import React from "react";
import { entityProfileModalBorders, entityProfileModalColors } from "../entityProfileModal.theme";
import EntityModalButton, { EntityModalButtonGroup } from "./EntityModalButton";

type EntityProfileSegmentedControlProps<TValue extends boolean | string | null> = {
  options: Array<{ label: string; value: TValue }>;
  value: TValue;
  onChange?: (value: TValue) => void;
};

export function EntityProfileSegmentedControl<TValue extends boolean | string | null>(
  props: EntityProfileSegmentedControlProps<TValue>
) {
  const isEditable = props.onChange !== undefined;

  return (
    <EntityModalButtonGroup>
      {props.options.map((option) => {
        const isSelected = props.value === option.value;

        return (
          <EntityModalButton
            key={option.label}
            disabled={!isEditable}
            kind={isSelected ? "blue" : "white"}
            size="sm"
            style={{
              cursor: isEditable ? undefined : "default",
              // A read-only control still has to read as selected, so the
              // selected button keeps full opacity while disabled.
              minWidth: option.label === "Unknown" ? 88 : 48,
              opacity: 1,
            }}
            onClick={() => props.onChange?.(option.value)}
          >
            {option.label}
          </EntityModalButton>
        );
      })}
    </EntityModalButtonGroup>
  );
}

type EntityProfileFieldRowProps = {
  label: string;
  value: React.ReactNode;
};

export function EntityProfileFieldRow(props: EntityProfileFieldRowProps) {
  return (
    <HStack
      alignItems="center"
      borderBottom={entityProfileModalBorders.default}
      justifyContent="space-between"
      minH="44px"
      spacing={4}
    >
      <Text
        color={entityProfileModalColors.labelText}
        flex="1 1 auto"
        fontSize="14px"
        fontWeight={700}
        lineHeight="1.25"
      >
        {props.label}
      </Text>
      <Box
        color={entityProfileModalColors.subduedText}
        flex="0 0 auto"
        fontSize="14px"
        textAlign="right"
      >
        {props.value}
      </Box>
    </HStack>
  );
}

type EntityProfileTextListProps = {
  emptyText?: string;
  items: string[] | null;
};

export function EntityProfileTextList(props: EntityProfileTextListProps) {
  const items = (props.items ?? []).filter((item) => item.trim() !== "");

  if (items.length === 0) {
    return <Text color={entityProfileModalColors.text}>{props.emptyText ?? ""}</Text>;
  }

  return (
    <UnorderedList
      color={entityProfileModalColors.text}
      fontSize="14px"
      marginStart={5}
      spacing={1}
    >
      {items.map((item, index) => (
        <ListItem key={`${item}-${index}`}>{item}</ListItem>
      ))}
    </UnorderedList>
  );
}

export function entityProfileListToText(items: string[] | null) {
  return (items ?? []).join("\n");
}

export function entityProfileTextToList(value: string) {
  return value
    .split("\n")
    .map((item) => item.trim())
    .filter((item) => item !== "");
}
